import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Activity, RefreshCw, AlertTriangle } from "lucide-react";

/**
 * Admin-only LLM usage meter.
 * Shows live LLM call counts (hour / 24h / 7d), per-provider breakdown,
 * top user spenders, and background-job attribution.
 *
 * Cost-leak detector — spot regressions where a UI element accidentally
 * starts hitting the ensemble on every render.
 */
export default function LLMUsageMeter() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/llm/meter");
      setStats(data);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 60 * 1000); // refresh every 60s
    return () => clearInterval(id);
  }, []);

  if (!stats) {
    return (
      <div className="bg-[#121212] border border-zinc-800 p-4" data-testid="llm-meter-loading">
        <div className="text-xs uppercase tracking-widest text-zinc-500">LLM Cost Meter</div>
        <div className="text-zinc-500 text-xs mt-2 font-mono">◢ Loading…</div>
      </div>
    );
  }

  const rateOk = stats.success_rate_24h >= 90;

  return (
    <div className="bg-[#121212] border border-zinc-800 p-4" data-testid="llm-meter">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">
            <Activity className="h-3 w-3" /> LLM Cost Meter
          </div>
          <div className="font-heading text-base font-bold uppercase tracking-tight">Token spend · 24h</div>
        </div>
        <button onClick={load} disabled={loading} className="text-zinc-500 hover:text-white p-1" data-testid="llm-meter-refresh">
          <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* Headline numbers */}
      <div className="grid grid-cols-4 gap-2 mb-3">
        <div className="border border-zinc-800 bg-[#0a0a0a] p-2" data-testid="llm-hour">
          <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-mono">Last hour</div>
          <div className="font-heading text-xl font-bold text-[#22C55E]">{stats.calls_last_hour}</div>
        </div>
        <div className="border border-zinc-800 bg-[#0a0a0a] p-2" data-testid="llm-day">
          <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-mono">Last 24h</div>
          <div className="font-heading text-xl font-bold text-white">{stats.calls_last_24h}</div>
        </div>
        <div className="border border-zinc-800 bg-[#0a0a0a] p-2" data-testid="llm-week">
          <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-mono">Last 7d</div>
          <div className="font-heading text-xl font-bold text-zinc-400">{stats.calls_last_7d}</div>
        </div>
        <div className="border border-[#22C55E]/30 bg-[#0a0a0a] p-2" data-testid="llm-saved"
             title={(stats.saved_by_reason_24h || []).map((r) => `${r.reason}: ${r.calls}`).join(" · ") || "AI calls avoided by open-trade / cooldown gates"}>
          <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-mono">Saved 24h</div>
          <div className="font-heading text-xl font-bold text-[#22C55E]">{stats.saved_last_24h ?? 0}</div>
        </div>
      </div>

      {/* Success rate */}
      <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest mb-3">
        <span className="text-zinc-500">Success rate 24h</span>
        <span className={`font-bold flex items-center gap-1 ${rateOk ? "text-[#22C55E]" : "text-[#EF4444]"}`}>
          {!rateOk && <AlertTriangle className="h-3 w-3" />}
          {stats.success_rate_24h}% · {stats.failures_last_24h} fails
        </span>
      </div>

      {/* By provider */}
      {Object.keys(stats.by_provider_24h || {}).length > 0 && (
        <div className="mb-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1.5">By provider</div>
          <div className="space-y-1">
            {Object.entries(stats.by_provider_24h).map(([p, n]) => (
              <div key={p} className="flex items-center justify-between text-xs">
                <span className="text-zinc-300 capitalize">{p}</span>
                <span className="font-mono text-zinc-400">{n}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top users */}
      {stats.top_users_24h?.length > 0 && (
        <div className="mb-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1.5">Top spenders · 24h</div>
          <div className="space-y-1">
            {stats.top_users_24h.map((u, i) => (
              <div key={u.actor} className="flex items-center justify-between text-xs" data-testid={`llm-top-${i}`}>
                <span className="text-zinc-300 truncate max-w-[60%]" title={u.email}>{u.email || u.actor}</span>
                <span className="font-mono text-zinc-400">{u.calls}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* System breakdown */}
      {stats.system_calls_24h?.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1.5">System jobs · 24h</div>
          <div className="space-y-1">
            {stats.system_calls_24h.map((s) => (
              <div key={s.source} className="flex items-center justify-between text-xs">
                <span className="text-zinc-400 font-mono">{s.source.replace("system:", "")}</span>
                <span className="font-mono text-zinc-500">{s.calls}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
